import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { Benutzer } from '@beholder/shared';
import { IDEE_STATUS, IDEE_STATUS_NAMEN, type IdeeStatus, type IdeenPapierkorbEintrag, type IdeenSitzung, type SitzungArt } from '@beholder/shared/module/ideen';
import { api } from '../../core/api';
import type { GlobalProps } from '../../core/modul';
import { Auswahl, Eingabe, Knopf, Laden, Meldung, datum, datumZeit, useAktion, useDaten } from '../../core/ui';
import { pfad, SEITE, sichtbarkeitText, sortieren, useAlleIdeen, useSitzungen, type Sortierung } from './daten';
import { IdeeKarte } from './IdeeKarte';
import { SitzungAnsicht } from './Sitzung';
import { Zeichen } from './Text';
import './stil.css';

/** Seite „Ideen“ oben in der Kopfleiste – auch ohne Projekt nutzbar (Marc, 04.11.). */
export function Seite({ ich }: GlobalProps) {
  const teil = useParams()['*']?.split('/')[0] || null;
  const sitzungId = teil && teil !== 'papierkorb' ? teil : null;
  const navigate = useNavigate();
  const [such, setSuch] = useSearchParams();
  const { liste, setListe, fehler, laden } = useSitzungen();
  const a = useAktion();

  const offen = useMemo(() => (liste ?? []).filter((s) => !s.geschlossen), [liste]);
  const geschlossen = useMemo(() => (liste ?? []).filter((s) => s.geschlossen), [liste]);

  const anlegen = async (art: SitzungArt, projektId: string | null = null) => {
    const s = await a.ausfuehren(() => api.post<IdeenSitzung>(pfad('/sitzungen'), { art, projektId }));
    if (s) {
      setListe((l) => (l && !l.some((x) => x.id === s.id) ? [...l, s] : l));
      navigate(`${SEITE}/${s.id}`, { replace: !!projektId });
    }
  };

  // Aus der Projektübersicht: /modul/ideen?neu=ideen&projekt=<id> legt gleich eine Runde für das Projekt an
  const neuAusProjekt = useRef(false);
  useEffect(() => {
    const art = such.get('neu');
    const projekt = such.get('projekt');
    if (!art || neuAusProjekt.current || (art !== 'ideen' && art !== 'suche')) return;
    neuAusProjekt.current = true;
    setSuch({}, { replace: true });
    void anlegen(art, projekt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [such]);

  const schliessen = async (id: string) => {
    const i = offen.findIndex((s) => s.id === id);
    const r = await a.ausfuehren(() => api.patch<IdeenSitzung>(pfad(`/sitzungen/${id}`), { geschlossen: true }));
    if (r) {
      setListe((l) => l?.map((s) => (s.id === id ? r : s)) ?? l);
      if (sitzungId === id) {
        const nachbar = offen[i + 1] ?? offen[i - 1];
        navigate(nachbar && nachbar.id !== id ? `${SEITE}/${nachbar.id}` : SEITE);
      }
    }
  };

  const umordnen = (ids: string[]) => {
    setListe((l) => {
      if (!l) return l;
      const pos = new Map(ids.map((id, i) => [id, i]));
      return [...l].sort((x, y) => (pos.get(x.id) ?? 1e6) - (pos.get(y.id) ?? 1e6));
    });
    void a.ausfuehren(() => api.put(pfad('/sitzungen/reihenfolge'), { ids: [...ids, ...geschlossen.map((s) => s.id)] }));
  };

  return (
    <div className="seite id-seite">
      <div className="seiten-kopf">
        <h1>Ideen</h1>
        <span className="leise klein">Ideen sammeln und recherchieren – für dich, geteilt oder für ein Projekt.</span>
      </div>
      <Leiste
        offen={offen}
        geschlossen={geschlossen}
        aktiv={sitzungId}
        anlegen={(art) => void anlegen(art)}
        schliessen={(id) => void schliessen(id)}
        umordnen={umordnen}
        umbenennen={(id, titel) =>
          void a.ausfuehren(async () => {
            const s = await api.patch<IdeenSitzung>(pfad(`/sitzungen/${id}`), { titel });
            setListe((l) => l?.map((x) => (x.id === id ? s : x)) ?? l);
          })
        }
        oeffnen={(id) =>
          void a.ausfuehren(async () => {
            const s = await api.patch<IdeenSitzung>(pfad(`/sitzungen/${id}`), { geschlossen: false });
            setListe((l) => l?.map((x) => (x.id === id ? s : x)) ?? l);
            navigate(`${SEITE}/${id}`);
          })
        }
      />
      <Meldung>{a.fehler || fehler}</Meldung>
      {!liste ? (
        <Laden />
      ) : teil === 'papierkorb' ? (
        <Papierkorb zurueck={() => void laden()} />
      ) : sitzungId ? (
        <SitzungAnsicht key={sitzungId} ich={ich} sitzungId={sitzungId} schliessen={(id) => void schliessen(id)} geaendert={() => void laden()} />
      ) : (
        <AlleIdeen ich={ich} sitzungen={liste} anlegen={(art) => void anlegen(art)} />
      )}
    </div>
  );
}

/** Leiste der Unterreiter: „Alle Ideen“, eigene offene Sitzungen (ziehen = ordnen, Doppelklick = umbenennen), + Ideen, + Suche, Geschlossene. */
function Leiste({
  offen,
  geschlossen,
  aktiv,
  anlegen,
  schliessen,
  umordnen,
  umbenennen,
  oeffnen,
}: {
  offen: IdeenSitzung[];
  geschlossen: IdeenSitzung[];
  aktiv: string | null;
  anlegen(art: SitzungArt): void;
  schliessen(id: string): void;
  umordnen(ids: string[]): void;
  umbenennen(id: string, titel: string): void;
  oeffnen(id: string): void;
}) {
  const [ziehe, setZiehe] = useState<string | null>(null);
  const [ueber, setUeber] = useState<{ id: string; davor: boolean } | null>(null);
  const [bearbeite, setBearbeite] = useState<{ id: string; titel: string } | null>(null);
  const [menue, setMenue] = useState(false);
  const menueRef = useRef<HTMLDivElement>(null);
  const leisteRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menue) return;
    const zu = (e: MouseEvent) => !menueRef.current?.contains(e.target as Node) && setMenue(false);
    document.addEventListener('mousedown', zu);
    return () => document.removeEventListener('mousedown', zu);
  }, [menue]);

  useEffect(() => {
    leisteRef.current?.querySelector('.id-reiter.active')?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [aktiv, offen.length]);

  const fallenLassen = () => {
    if (!ziehe || !ueber || ziehe === ueber.id) return;
    const ids = offen.map((s) => s.id).filter((id) => id !== ziehe);
    const i = ids.indexOf(ueber.id);
    ids.splice(ueber.davor ? i : i + 1, 0, ziehe);
    umordnen(ids);
  };

  const fertigUmbenennen = () => {
    if (bearbeite?.titel.trim()) {
      const alt = offen.find((s) => s.id === bearbeite.id)?.titel;
      if (alt !== bearbeite.titel.trim()) umbenennen(bearbeite.id, bearbeite.titel.trim());
    }
    setBearbeite(null);
  };

  return (
    <nav className="id-leiste" aria-label="Unterreiter der Ideen">
      <div className="id-reiter-zeile" ref={leisteRef}>
        <NavLink end to={SEITE} className="id-reiter id-reiter-alle">
          {Zeichen.stern} Alle Ideen
        </NavLink>
        {offen.map((s) => {
          const markiert = ueber?.id === s.id && ziehe && ziehe !== s.id ? (ueber.davor ? ' id-davor' : ' id-danach') : '';
          const darfUmbenennen = s.meineStufe === 'schreiben';
          if (bearbeite?.id === s.id) {
            return (
              <Eingabe
                key={s.id}
                className="id-reiter-eingabe"
                value={bearbeite.titel}
                autoFocus
                maxLength={120}
                aria-label="Neuer Name"
                onChange={(e) => setBearbeite({ id: s.id, titel: e.target.value })}
                onBlur={fertigUmbenennen}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') fertigUmbenennen();
                  if (e.key === 'Escape') setBearbeite(null);
                }}
              />
            );
          }
          return (
            <NavLink
              key={s.id}
              to={`${SEITE}/${s.id}`}
              className={`id-reiter${ziehe === s.id ? ' id-zieht' : ''}${markiert}`}
              draggable
              title={`${s.titel} · ${sichtbarkeitText(s)}${darfUmbenennen ? ' – Doppelklick zum Umbenennen' : ''}, ziehen zum Ordnen`}
              onDragStart={(e) => {
                setZiehe(s.id);
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', s.id);
              }}
              onDragOver={(e) => {
                if (!ziehe) return;
                e.preventDefault();
                const r = e.currentTarget.getBoundingClientRect();
                setUeber({ id: s.id, davor: e.clientX < r.left + r.width / 2 });
              }}
              onDrop={(e) => {
                e.preventDefault();
                fallenLassen();
                setZiehe(null);
                setUeber(null);
              }}
              onDragEnd={() => {
                setZiehe(null);
                setUeber(null);
              }}
              onDoubleClick={(e) => {
                if (!darfUmbenennen) return;
                e.preventDefault();
                setBearbeite({ id: s.id, titel: s.titel });
              }}
            >
              <span className="id-reiter-symbol">{s.art === 'suche' ? Zeichen.lupe : Zeichen.stern}</span>
              <span className="id-reiter-titel">{s.titel}</span>
              <span className={`id-reiter-sicht id-sicht-${s.sichtbarkeit}`} aria-label={sichtbarkeitText(s)}>
                {s.sichtbarkeit === 'projekt' ? Zeichen.ordner : s.sichtbarkeit === 'geteilt' ? Zeichen.leute : null}
              </span>
              {s.ideenAnzahl > 0 && <span className="id-zahl">{s.ideenAnzahl}</span>}
              <button
                type="button"
                className="id-reiter-zu"
                aria-label={`„${s.titel}“ schließen`}
                title="Schließen (nur für dich, bleibt unter „Geschlossene“)"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  schliessen(s.id);
                }}
              >
                ×
              </button>
            </NavLink>
          );
        })}
      </div>
      <div className="id-leiste-knoepfe">
        <Knopf klein onClick={() => anlegen('ideen')} title="Neue Ideen-Runde mit der KI (erst nur für dich)">
          + Ideen
        </Knopf>
        <Knopf klein onClick={() => anlegen('suche')} title="Neue Suche in deinen Projekten und im Internet (erst nur für dich)">
          + Suche
        </Knopf>
        <div className="id-menue-halter" ref={menueRef}>
          <Knopf klein art="leise" aria-expanded={menue} onClick={() => setMenue((m) => !m)} aria-label="Weitere">
            {geschlossen.length ? `Geschlossene (${geschlossen.length})` : 'Mehr'} ▾
          </Knopf>
          {menue && (
            <div className="id-menue id-menue-rechts" role="menu">
              {geschlossen.map((s) => (
                <div key={s.id} className="id-menue-zeile">
                  <NavLink to={`${SEITE}/${s.id}`} onClick={() => setMenue(false)} role="menuitem">
                    {s.art === 'suche' ? Zeichen.lupe : Zeichen.stern} {s.titel}
                  </NavLink>
                  <Knopf klein art="leise" onClick={() => (setMenue(false), oeffnen(s.id))}>
                    öffnen
                  </Knopf>
                </div>
              ))}
              <div className="id-menue-zeile">
                <NavLink to={`${SEITE}/papierkorb`} onClick={() => setMenue(false)} role="menuitem">
                  {Zeichen.x} Mein Papierkorb
                </NavLink>
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}

/** Übersicht über alle Ideen, die ich sehen darf. */
function AlleIdeen({ ich, sitzungen, anlegen }: { ich: Benutzer; sitzungen: IdeenSitzung[]; anlegen(art: SitzungArt): void }) {
  const { ideen, fehler } = useAlleIdeen();
  const [status, setStatus] = useState<IdeeStatus | 'aktiv'>('aktiv');
  const [sortierung, setSortierung] = useState<Sortierung>('stimmen');
  const [projekt, setProjekt] = useState('');
  const [suche, setSuche] = useState('');
  const stufen = useMemo(() => new Map(sitzungen.map((s) => [s.id, s.meineStufe])), [sitzungen]);
  const projekte = useMemo(() => {
    const m = new Map<string, string>();
    for (const i of ideen ?? []) if (i.projektId) m.set(i.projektId, i.projektName ?? '');
    return [...m].sort((x, y) => x[1].localeCompare(y[1]));
  }, [ideen]);

  const sichtbar = useMemo(() => {
    const s = suche.trim().toLowerCase();
    return sortieren(
      (ideen ?? []).filter(
        (i) =>
          (status === 'aktiv' ? i.status !== 'verworfen' : i.status === status) &&
          (!projekt || (projekt === '-' ? !i.projektId : i.projektId === projekt)) &&
          (!s || `${i.titel} ${i.beschreibung} ${i.risiken}`.toLowerCase().includes(s)),
      ),
      sortierung,
    );
  }, [ideen, status, sortierung, suche, projekt]);

  if (sitzungen.length === 0) {
    return (
      <div className="id-start">
        <h2>Ideen sammeln und recherchieren</h2>
        <p className="leise">
          Jeder Unterreiter ist eine eigene, gespeicherte Sitzung mit der KI. Neue Unterreiter siehst erst nur du – teilen kannst du sie mit
          einzelnen Personen oder einem Projekt zuordnen. Aus einer guten Idee lässt sich direkt ein Projekt machen.
        </p>
        <div className="id-start-wahl">
          <div className="karte">
            <h3>{Zeichen.stern} Ideen-Runde</h3>
            <p className="leise">Brainstorming mit der KI: Ideen mit grober Schätzung zu Kosten, Aufwand und Risiken, dann gemeinsam bewerten.</p>
            <Knopf art="haupt" onClick={() => anlegen('ideen')}>
              + Ideen-Runde starten
            </Knopf>
          </div>
          <div className="karte">
            <h3>{Zeichen.lupe} Suche</h3>
            <p className="leise">In deinen Projekten suchen und auf Wunsch im Internet recherchieren – Quellen nur als Text.</p>
            <Knopf art="haupt" onClick={() => anlegen('suche')}>
              + Suche starten
            </Knopf>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="id-alle">
      <div className="id-filter">
        <Eingabe value={suche} onChange={(e) => setSuche(e.target.value)} placeholder="Ideen durchsuchen …" aria-label="Ideen durchsuchen" />
        <Auswahl value={projekt} onChange={(e) => setProjekt(e.target.value)} aria-label="Projekt">
          <option value="">alle Projekte und ohne</option>
          <option value="-">nur ohne Projekt</option>
          {projekte.map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </Auswahl>
        <Auswahl value={status} onChange={(e) => setStatus(e.target.value as IdeeStatus | 'aktiv')} aria-label="Status">
          <option value="aktiv">alle außer verworfene</option>
          {IDEE_STATUS.map((s) => (
            <option key={s} value={s}>
              nur {IDEE_STATUS_NAMEN[s]}
            </option>
          ))}
        </Auswahl>
        <Auswahl value={sortierung} onChange={(e) => setSortierung(e.target.value as Sortierung)} aria-label="Sortieren">
          <option value="stimmen">nach Stimmen</option>
          <option value="neu">neueste zuerst</option>
          <option value="alt">älteste zuerst</option>
        </Auswahl>
      </div>
      <Meldung>{fehler}</Meldung>
      {!ideen ? (
        <Laden />
      ) : sichtbar.length === 0 ? (
        <p className="leise">{ideen.length ? 'Keine Idee passt zum Filter.' : 'Noch keine Ideen – öffne einen Unterreiter oder starte oben eine neue Runde.'}</p>
      ) : (
        <div className="id-raster-karten">
          {sichtbar.map((i) => (
            <IdeeKarte key={i.id} idee={i} ichId={ich.id} darfSchreiben={stufen.get(i.sitzungId) === 'schreiben'} ziele={null} mitRunde />
          ))}
        </div>
      )}
    </div>
  );
}

/** Eigener Papierkorb: gelöschte Unterreiter ohne Projekt (180 Tage). Projekt-Unterreiter liegen im Papierkorb des Projekts. */
function Papierkorb({ zurueck }: { zurueck(): void }) {
  const { daten, fehler, neuLaden } = useDaten(() => api.get<{ tage: number; eintraege: IdeenPapierkorbEintrag[] }>(pfad('/papierkorb')), []);
  const a = useAktion();
  const holen = (id: string) =>
    void a.ausfuehren(async () => {
      await api.post(pfad(`/papierkorb/${id}/wiederherstellen`));
      await neuLaden();
      zurueck();
    });
  const weg = (e: IdeenPapierkorbEintrag) => {
    if (!confirm(`„${e.titel}“ endgültig löschen? Das lässt sich nicht rückgängig machen.`)) return;
    void a.ausfuehren(async () => {
      await api.delete(pfad(`/papierkorb/${e.id}`));
      await neuLaden();
    });
  };
  return (
    <div className="stapel">
      <div className="reihe">
        <h2>Mein Papierkorb</h2>
        <span className="leise klein">
          Gelöschte Unterreiter ohne Projekt bleiben {daten?.tage ?? 180} Tage. Unterreiter eines Projekts liegen im Papierkorb des Projekts.
        </span>
      </div>
      <Meldung>{a.fehler || fehler}</Meldung>
      {!daten ? (
        <Laden />
      ) : daten.eintraege.length === 0 ? (
        <p className="leise">Der Papierkorb ist leer.</p>
      ) : (
        <ul className="id-korb">
          {daten.eintraege.map((e) => (
            <li key={e.id} className="karte">
              <div>
                <strong>{e.titel}</strong> <span className="leise klein">· {e.art} · {e.details}</span>
                <div className="leise klein">
                  gelöscht {datumZeit(e.geloeschtAm)} · endgültig weg am {datum(e.endgueltigAm)}
                </div>
              </div>
              <div className="reihe">
                <Knopf klein onClick={() => holen(e.id)} disabled={a.laeuft}>
                  Zurückholen
                </Knopf>
                <Knopf klein art="gefahr" onClick={() => weg(e)} disabled={a.laeuft}>
                  Endgültig löschen
                </Knopf>
              </div>
            </li>
          ))}
        </ul>
      )}
      <Link to={SEITE}>← Zurück zu den Ideen</Link>
    </div>
  );
}
